import { useState, useCallback } from 'react';

import type { BadStatusError } from '@confluence/network';

import type { FetchOptions } from './fetchResponse';
import { useFetchData } from './useFetchData';
import { handleError } from './handleError';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback';

type MutationResult<TData> = {
	data: TData | undefined;
	loading: boolean;
	error: Error | undefined;
};

type UseExperimentalMutationParams<TData, TVariables, TResponse> = FetchOptions<
	TVariables,
	TResponse
> & {
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void;
	onError?: (error: Error | BadStatusError) => Promise<void> | void;
	maxRetries?: number;
};

type MutationFunctionOptions<TData, TVariables> = {
	variables: TVariables;
	update?: (cache: null, result: { data?: TData | null }) => void;
};

export function useExperimentalMutation<TData, TVariables, TResponse>({
	url,
	method,
	headers,
	body,
	credentials,
	parseResult,
	onCompleted,
	onError,
	maxRetries = 0,
}: UseExperimentalMutationParams<TData, TVariables, TResponse>): [
	(options: MutationFunctionOptions<TData, TVariables>) => Promise<MutationResult<TData>>,
	MutationResult<TData>,
] {
	const { fetchData } = useFetchData<TData, TVariables, TResponse>({
		url,
		method,
		headers,
		body,
		credentials,
		parseResult,
	});

	const fetchDataWithRetry = useCallback(
		async (variables: TVariables, count = 0): Promise<TData> => {
			try {
				return await fetchData(variables);
			} catch (error) {
				if (count < maxRetries && error?.response?.status === 409) {
					return fetchDataWithRetry(variables, ++count);
				}
				throw error;
			}
		},
		[maxRetries, fetchData],
	);

	return useExperimentalMutationBase<TData, TVariables>({
		fetchData: fetchDataWithRetry,
		onCompleted,
		onError,
	});
}

type UseExperimentalMutationBaseParams<TData, TVariables> = {
	fetchData: (variables: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void;
	onError?: (error: Error | BadStatusError) => Promise<void> | void;
};

export function useExperimentalMutationBase<TData, TVariables>({
	fetchData,
	onCompleted,
	onError,
}: UseExperimentalMutationBaseParams<TData, TVariables>): [
	(options: MutationFunctionOptions<TData, TVariables>) => Promise<MutationResult<TData>>,
	MutationResult<TData>,
] {
	const [result, setResult] = useState<MutationResult<TData>>({
		data: undefined,
		loading: false,
		error: undefined,
	});

	const mutate = useStableCallback(
		async ({ variables, update }: MutationFunctionOptions<TData, TVariables>) => {
			setResult({ data: undefined, loading: true, error: undefined });

			try {
				const data = await fetchData(variables);
				// The `update` function matches the Apollo useMutation API for compatibility
				// This hook does not implement a cache, so `cache` is always null
				// This can possibly be replaced by onCompleted
				update?.(null, { data });

				const newResult = { data, loading: false, error: undefined };
				setResult(newResult);

				onCompleted?.(data);

				return newResult;
			} catch (error) {
				const finalError = await handleError(error, onError);
				const newResult = { data: undefined, loading: false, error: finalError };
				setResult(newResult);
				throw finalError;
			}
		},
	);

	return [mutate, result];
}
